import type { RouteObject } from "react-router";
import {
  AutomationArtifactTimeCrumbLabel,
  AutomationNameCrumbLabel,
  AutomationRunTimeCrumbLabel,
} from "../../features/automations/automationCrumbLabels.js";
import { CustomerDetailPage } from "../../features/customers/customerDetailPage.js";
import { CustomerNameCrumbLabel } from "../../features/customers/customerNameCrumbLabel.js";
import { CustomerProfileGuard } from "../../features/customers/customerProfileGuard.js";
import { CustomersPage } from "../../features/customers/customersPage.js";
import { DefaultPlaybookRedirect } from "../../features/links/defaultPlaybookRedirect.js";
import { SignalDrilldownPage } from "../../features/signals/drilldown/signalDrilldownPage.js";
import { SignalMatchSlideoverContainer } from "../../features/signals/drilldown/signalMatchSlideoverContainer.js";
import { SignalsPage } from "../../features/signals/overview/signalsPage.js";
import { SignalNameCrumbLabel } from "../../features/signals/signalNameCrumbLabel.js";
import { PerformanceDashboard } from "../../features/teammatePerformance/dashboard/performanceDashboard.js";
import {
  PlaybookCriterionNameCrumbLabel,
  TeammateNameCrumbLabel,
} from "../../features/teammatePerformance/performanceCrumbLabels.js";
import { PerformanceDefaultRedirect } from "../../features/teammatePerformance/performanceDefaultRedirect.js";
import { PlaybookMonthInReview } from "../../features/teammatePerformance/playbookMonthInReview.js";
import { TrainingTeamPageByCustomer } from "../../features/teammatePerformance/teamPerformanceByCustomer.js";
import { TrainingTeamPageByPlaybook } from "../../features/teammatePerformance/teamPerformanceByPlaybook.js";
import { TrainingTeamPageByTeam } from "../../features/teammatePerformance/teamPerformanceByTeam.js";
import { TopicsPage } from "../../features/topics/overview/topicsPage.js";
import { AutomationArtifactDetailPage } from "../../pages/automations/automationArtifactDetailPage.js";
import { AutomationDetailPage } from "../../pages/automations/automationDetailPage.js";
import { AutomationNewPage } from "../../pages/automations/automationNewPage.js";
import { AutomationOverviewTab } from "../../pages/automations/automationOverviewTab.js";
import { AutomationResultsTab } from "../../pages/automations/automationResultsTab.js";
import { AutomationRunDetailPage } from "../../pages/automations/automationRunDetailPage.js";
import { AutomationRunsTab } from "../../pages/automations/automationRunsTab.js";
import { AutomationsPage } from "../../pages/automations/automationsPage.js";
import { AutomationsViewerGuard } from "../../pages/automations/automationsViewerGuard.js";
import {
  AllReportsPage,
  resolveReportTitleByTemplateId,
} from "../../pages/performanceReports/allReportsPage.js";
import { ReportDetailPage } from "../../pages/performanceReports/reportDetailPage.js";
import { TrainingTeamPage } from "../../pages/trainingTeamPage.js";
import { appPaths } from "../appPaths.js";
import { defineHandle } from "../navigation/appRouteHandle.js";
import { PerformanceNavigationMenu } from "../navigation/menu/performanceNavigationMenu.js";
import { performanceSections } from "../navigation/performanceSections.js";
import { WorkspaceId } from "../navigation/workspaces.js";
import { requireRouteParam } from "../routeParams.js";
import { GradingTupleRedirect, ParamsRedirect } from "./compatRedirects.js";
import {
  createCriterionDrilldownRoute,
  createGradingSlideoverRoute,
} from "./gradingRoutes.js";
import { createPlaybookRoute } from "./playbookRoute.js";
import { createTeammatePageRoute } from "./teammatePageRoute.js";

/** The top-level routes of the Performance workspace. */
export function createPerformanceWorkspaceRoutes(): RouteObject[] {
  return [
    {
      // Redirects to the teammate's default playbook: last selected, then the
      // reporting-tree inference, and finally the first playbook.
      path: appPaths.playbooks.pattern,
      element: <DefaultPlaybookRedirect />,
      handle: defineHandle({
        workspaceId: WorkspaceId.Performance,
        navigationMenu: <PerformanceNavigationMenu />,
      }),
    },
    createPlaybookRoute([
      {
        path: appPaths.playbook.pattern,
        Component: TrainingTeamPage,
        handle: defineHandle({
          workspaceId: WorkspaceId.Performance,
          navigationMenu: <PerformanceNavigationMenu />,
        }),
        children: [
          { index: true, element: <PerformanceDefaultRedirect /> },
          {
            path: "dashboard",
            Component: PerformanceDashboard,
            handle: defineHandle({
              crumbs: () => [{ label: performanceSections.overview.label }],
            }),
          },
          {
            path: "teammates",
            Component: TrainingTeamPageByTeam,
            handle: defineHandle({
              crumbs: () => [{ label: performanceSections.team.label }],
            }),
          },
          {
            path: "groups",
            Component: TrainingTeamPageByTeam,
            handle: defineHandle({
              crumbs: () => [{ label: performanceSections.team.label }],
            }),
          },
          {
            path: "customers",
            element: (
              <CustomerProfileGuard>
                <CustomersPage />
              </CustomerProfileGuard>
            ),
            handle: defineHandle({
              crumbs: () => [{ label: performanceSections.customers.label }],
              pageContext: () => ({ page: "customers" }),
            }),
          },
          {
            path: "customers/:accountId",
            element: (
              <CustomerProfileGuard>
                <CustomerDetailPage />
              </CustomerProfileGuard>
            ),
            handle: defineHandle({
              crumbs: ({ params }) => [
                {
                  label: performanceSections.customers.label,
                  href: appPaths.playbookCustomers.build({
                    playbookId: requireRouteParam(params, "playbookId"),
                  }),
                },
                {
                  label: (
                    <CustomerNameCrumbLabel accountId={params.accountId} />
                  ),
                },
              ],
              pageContext: ({ params }) => ({
                page: "customer",
                accountId: params.accountId,
              }),
            }),
            children: [
              createGradingSlideoverRoute({
                path: "customer-interactions/:gradingId",
                buildCriterionPath: (params, criterionId) =>
                  appPaths.playbookCustomerCallCriterion.build({
                    playbookId: requireRouteParam(params, "playbookId"),
                    accountId: requireRouteParam(params, "accountId"),
                    gradingId: requireRouteParam(params, "gradingId"),
                    criterionId,
                  }),
              }),
            ],
          },
          {
            path: "customer-interactions",
            Component: TrainingTeamPageByCustomer,
            handle: defineHandle({
              crumbs: () => [{ label: performanceSections.calls.label }],
              pageContext: () => ({ page: "playbookCalls" }),
            }),
            children: [
              createGradingSlideoverRoute({
                path: ":gradingId",
                buildCriterionPath: (params, criterionId) =>
                  appPaths.playbookCallCriterion.build({
                    playbookId: requireRouteParam(params, "playbookId"),
                    gradingId: requireRouteParam(params, "gradingId"),
                    criterionId,
                  }),
              }),
            ],
          },
          // Backwards-compatibility redirects for old by-customer URLs.
          {
            path: "accounts",
            element: (
              <ParamsRedirect
                buildTo={(params) =>
                  appPaths.playbookCalls.build({
                    playbookId: requireRouteParam(params, "playbookId"),
                  })
                }
              />
            ),
          },
          {
            path: "accounts/teammates/:teammateId/accounts/:accountId/*",
            element: (
              <GradingTupleRedirect
                buildTo={(params, gradingId) =>
                  appPaths.playbookCall.build({
                    playbookId: requireRouteParam(params, "playbookId"),
                    gradingId,
                  })
                }
              />
            ),
          },
          {
            path: "overview",
            element: (
              <ParamsRedirect
                buildTo={(params) =>
                  appPaths.playbookCriteria.build({
                    playbookId: requireRouteParam(params, "playbookId"),
                  })
                }
              />
            ),
          },
          {
            // The Agents (automations) section: viewable by editors and
            // reviewers (reviewers get read-only controls); team members are
            // redirected away.
            Component: AutomationsViewerGuard,
            children: [
              {
                path: "automations",
                handle: defineHandle({
                  crumbs: ({ pathname }) => [
                    {
                      label: performanceSections.automations.label,
                      href: pathname,
                    },
                  ],
                  pageContext: () => ({ page: "automations" }),
                }),
                children: [
                  {
                    index: true,
                    Component: AutomationsPage,
                  },
                  {
                    path: "new",
                    Component: AutomationNewPage,
                    handle: defineHandle({
                      crumbs: () => [{ label: "New automation" }],
                      pageContext: () => ({ page: "automationConfiguration" }),
                    }),
                  },
                  {
                    path: ":automationId",
                    handle: defineHandle({
                      crumbs: ({ params, pathname }) => [
                        {
                          label: (
                            <AutomationNameCrumbLabel
                              automationId={params.automationId}
                            />
                          ),
                          href: pathname,
                        },
                      ],
                    }),
                    children: [
                      {
                        // The agent detail chrome with its Overview | Results
                        // | Runs tabs; the standalone run/result pages below
                        // share its path prefix but not its layout.
                        Component: AutomationDetailPage,
                        handle: defineHandle({
                          pageContext: ({ params }) => ({
                            page: "automationConfiguration",
                            automationId: params.automationId,
                          }),
                        }),
                        children: [
                          {
                            index: true,
                            Component: AutomationOverviewTab,
                          },
                          {
                            path: "results",
                            Component: AutomationResultsTab,
                            handle: defineHandle({
                              crumbs: () => [{ label: "Results" }],
                            }),
                          },
                          {
                            path: "runs",
                            Component: AutomationRunsTab,
                            handle: defineHandle({
                              crumbs: () => [{ label: "Runs" }],
                            }),
                          },
                        ],
                      },
                      {
                        path: "runs",
                        handle: defineHandle({
                          crumbs: ({ pathname }) => [
                            { label: "Runs", href: pathname },
                          ],
                        }),
                        children: [
                          {
                            path: ":runId",
                            Component: AutomationRunDetailPage,
                            handle: defineHandle({
                              crumbs: ({ params }) => [
                                {
                                  label: (
                                    <AutomationRunTimeCrumbLabel
                                      automationId={params.automationId}
                                      runId={params.runId}
                                    />
                                  ),
                                },
                              ],
                              pageContext: ({ params }) => ({
                                page: "automationRun",
                                automationId: params.automationId,
                                runId: params.runId,
                              }),
                            }),
                          },
                        ],
                      },
                      {
                        // A saved artifact, opened from the agent's Results
                        // list or the notification email's "View report"
                        // link. A standalone page (not nested in the detail
                        // tabs).
                        path: "results",
                        handle: defineHandle({
                          crumbs: ({ pathname }) => [
                            { label: "Results", href: pathname },
                          ],
                        }),
                        children: [
                          {
                            path: ":artifactId",
                            Component: AutomationArtifactDetailPage,
                            handle: defineHandle({
                              crumbs: ({ params }) => [
                                {
                                  label: (
                                    <AutomationArtifactTimeCrumbLabel
                                      automationId={params.automationId}
                                      artifactId={params.artifactId}
                                    />
                                  ),
                                },
                              ],
                              pageContext: ({ params }) => ({
                                page: "automationArtifact",
                                automationId: params.automationId,
                                artifactId: params.artifactId,
                              }),
                            }),
                          },
                        ],
                      },
                    ],
                  },
                ],
              },
            ],
          },
          {
            path: "signals",
            handle: defineHandle({
              crumbs: ({ pathname }) => [
                { label: performanceSections.signals.label, href: pathname },
              ],
              pageContext: () => ({ page: "signals" }),
            }),
            children: [
              {
                index: true,
                Component: SignalsPage,
              },
              {
                path: ":signalId",
                Component: SignalDrilldownPage,
                handle: defineHandle({
                  crumbs: ({ params }) => [
                    {
                      label: (
                        <SignalNameCrumbLabel signalId={params.signalId} />
                      ),
                    },
                  ],
                  pageContext: ({ params }) => ({
                    page: "signalPerformance",
                    signalId: params.signalId,
                  }),
                }),
                children: [
                  {
                    path: "matches/:matchId",
                    Component: SignalMatchSlideoverContainer,
                  },
                ],
              },
            ],
          },
          {
            path: "topics",
            handle: defineHandle({
              crumbs: () => [{ label: performanceSections.topics.label }],
              pageContext: () => ({ page: "topics" }),
            }),
            children: [
              { index: true, Component: TopicsPage },
              {
                path: ":topicId",
                Component: TopicsPage,
                handle: defineHandle({
                  pageContext: ({ params }) => ({ topicId: params.topicId }),
                }),
              },
            ],
          },
          {
            path: "criteria",
            handle: defineHandle({
              crumbs: ({ pathname }) => [
                { label: performanceSections.playbook.label, href: pathname },
              ],
              pageContext: () => ({ page: "playbookCriteria" }),
            }),
            children: [
              { index: true, Component: TrainingTeamPageByPlaybook },
              {
                path: ":criterionId",
                Component: TrainingTeamPageByPlaybook,
                handle: defineHandle({
                  crumbs: ({ params }) => [
                    {
                      label: (
                        <PlaybookCriterionNameCrumbLabel
                          playbookId={params.playbookId}
                          criterionId={params.criterionId}
                        />
                      ),
                    },
                  ],
                  pageContext: ({ params }) => ({
                    page: "playbookCriterion",
                    criterionId: params.criterionId,
                  }),
                }),
                children: [
                  createCriterionDrilldownRoute({
                    path: "teammate/:teammateId",
                    // The criterion page's descriptor is inherited; this only
                    // narrows it to the teammate being drilled into.
                    pageContext: ({ params }) => ({
                      teammateId: params.teammateId,
                    }),
                    buildCriterionPath: (params, criterionId) =>
                      appPaths.playbookCriterionTeammateCall.build({
                        playbookId: requireRouteParam(params, "playbookId"),
                        teammateId: requireRouteParam(params, "teammateId"),
                        criterionId,
                        gradingId: requireRouteParam(params, "gradingId"),
                      }),
                    buildTupleRedirectTo: (params, gradingId) =>
                      appPaths.playbookCriterionTeammateCall.build({
                        playbookId: requireRouteParam(params, "playbookId"),
                        teammateId: requireRouteParam(params, "teammateId"),
                        criterionId: requireRouteParam(params, "criterionId"),
                        gradingId,
                      }),
                  }),
                ],
              },
            ],
          },
        ],
      },
      {
        path: appPaths.playbookReview.pattern,
        Component: PlaybookMonthInReview,
        handle: defineHandle({
          workspaceId: WorkspaceId.Performance,
          navigationMenu: <PerformanceNavigationMenu />,
        }),
      },
      createTeammatePageRoute({
        crumbs: ({ params, pathname }) => [
          {
            label: performanceSections.team.label,
            href: params.playbookId
              ? performanceSections.team.path.build({
                  playbookId: params.playbookId,
                })
              : undefined,
          },
          {
            label: <TeammateNameCrumbLabel teammateId={params.teammateId} />,
            href: pathname,
          },
        ],
      }),
      {
        path: appPaths.playbookReports.pattern,
        handle: defineHandle({
          workspaceId: WorkspaceId.Performance,
          navigationMenu: <PerformanceNavigationMenu />,
          crumbs: ({ pathname }) => [
            { label: performanceSections.reports.label, href: pathname },
          ],
        }),
        children: [
          {
            index: true,
            Component: AllReportsPage,
          },
          {
            path: ":reportId",
            Component: ReportDetailPage,
            handle: defineHandle({
              crumbs: ({ params }) => [
                {
                  label: params.reportId
                    ? resolveReportTitleByTemplateId(params.reportId)
                    : "Report",
                },
              ],
              pageContext: ({ params }) => ({
                page: "performanceReport",
                reportId: params.reportId,
              }),
            }),
          },
        ],
      },
    ]),
  ];
}
